import { useQueryClient } from '@tanstack/react-query';
import type { FC } from 'react';
import React, { createContext, useCallback, useContext, useMemo } from 'react';
import type {
  InitiatedCartPaymentDto,
  ListActivePaymentDto,
  PaymentMethodDto,
} from '../../generated-api';
import { PaymentState } from '../../generated-api';
import { useActivePaymentStore } from './ActivePaymentStore';
import config from '../config';
import { useActivePayments } from '../hooks/useActivePayments';
import { useMutateCartPayV3 } from '../hooks/useCartApi/useMutatePayCartV2';
import useDeviceId from '../hooks/useDeviceId';
import { useAutomaticPrintingPlacement } from '../hooks/useiOSPlacementId';

import FocusedPaymentDialog from '../pages/Menu/checkout-sidebar/Cart/payment/FocusedPaymentDialog/FocusedPaymentDialog';
import { toPayCartDto } from '../utils/cart';
import { sendIosMessageCashPaymentSuccess } from '../utils/checkout';
import { useLocation } from './LocationContext';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useNavigate } from 'react-router-dom';
import { useScreenSize } from './ScreenSizeContext';
import softpayClient from '../utils/softpay/SoftPayClient';

import { useTipDialogRemake } from '../hooks/useTipDialog';
import useCreatePendingPayment from '../hooks/usePendingPaymentApi/useCreatePendingPayment';
import { getEffectiveTotalPrice } from '../hooks/useTotalPrice';
import { useTerminalModeEnabled } from '../hooks/useTerminalModeEnabled';
import useSetNewCart from '../hooks/useSetNewCart';
import { useCartStore } from './cart/CartStore';
import { useDiscounts } from './DiscountStore';
import { setPaymentTracingActive } from '../tracing';
import { useTerminalStore } from './TerminalStore';
import { usePaymentSubscription } from '../hooks/usePaymentSubscription';
import { useFocusedPaymentStore } from './FocusedPaymentStore';
import type {
  CreateOrderLineDto,
  CreateOrderLineOptionDto,
  DeliveryMethodDto,
  SoftpayDirectSaleRequest,
} from '@ordr-as/ordr-service-types/epos';
import { useDirectSale } from '@ordr-as/ordr-service-types/epos';

interface InitiatePaymentArgs {
  paymentMethod: PaymentMethodDto;
  deliveryMethod: DeliveryMethodDto;
  tableNumber?: string;
  comment?: string;
  onSuccess?: () => void;
  onError?: () => void;
  dynamicFields?: { [key: string]: string };
  giftCardPhoneNumber?: string;
  tipAmount?: number;
  vatType: 'EAT_IN' | 'TAKEAWAY';
}
export interface PaymentContextType {
  initiatePayment: (initiatePaymentArgs: InitiatePaymentArgs) => void;
  activePayments?: ListActivePaymentDto[];
}

export const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

interface ProviderProps {
  children?: React.ReactNode;
}

const toCreateOrderLineOptionDto = (option: {
  itemVariantId: string;
  quantity: number;
  type: string;
}): CreateOrderLineOptionDto => ({
  menuItem: option.itemVariantId,
  menuItemType: option.type as CreateOrderLineOptionDto['menuItemType'],
  quantity: option.quantity,
});

const toCreateOrderLineDto = (orderLine: {
  itemVariantId: string;
  quantity: number;
  options: {
    itemVariantId: string;
    quantity: number;
    type: string;
  }[];
  menuSectionId?: string;
  sectionProductId?: string;
  comment?: string;
  openPrice?: boolean;
  courseId?: string;
  unitQuantity?: number;
  compositeProductId?: string;
  isCompositeProduct: boolean;
  name: string;
  price: number;
  takeawayPrice?: number;
  components?: typeof orderLine[];
}): CreateOrderLineDto => ({
  comment: orderLine.comment,
  components: orderLine.components?.map(toCreateOrderLineDto),
  compositeProductId: orderLine.compositeProductId,
  courseId: orderLine.courseId,
  isCompositeProduct: orderLine.isCompositeProduct,
  menuItem: orderLine.itemVariantId,
  menuSectionId: orderLine.menuSectionId,
  name: orderLine.name,
  openPriceAmount: orderLine.openPrice ? orderLine.price : undefined,
  options: orderLine.options.map(toCreateOrderLineOptionDto),
  quantity: orderLine.quantity,
  sectionProductId: orderLine.sectionProductId,
  unitQuantity: orderLine.unitQuantity,
});

const toDirectSaleTip = (askForTip: boolean): SoftpayDirectSaleRequest['tip'] => {
  if (!askForTip) {
    return { type: 'SkipPrompt' };
  }

  return {
    type: 'Percentage',
    values: [5, 10, 15, 20],
  };
};

export const PaymentContextProvider: FC<ProviderProps> = ({ children }) => {
  const payCartMutation = useMutateCartPayV3();
  const throwErrorToBoundary = useErrorHandler();
  const { location } = useLocation();
  const discounts = useDiscounts();

  const getCartSnapshot = useCartStore((state) => state.getCartSnapshot);

  const setNewCart = useSetNewCart();

  const queryClient = useQueryClient();
  const deviceId = useDeviceId();
  const { defaultPlacement } = useAutomaticPrintingPlacement();
  const navigate = useNavigate();
  const { isPhone } = useScreenSize();

  const { createPendingPayment } = useCreatePendingPayment();
  const initiateDirectSaleMutation = useDirectSale();

  const isTerminalModeEnabled = useTerminalModeEnabled();

  const focusedPayment = useFocusedPaymentStore((s) => s.focusedPayment);

  const { promptForTips, TipsDialogUI } = useTipDialogRemake();

  const subscribeToPayment = usePaymentSubscription();

  const onPaymentCaptured = useCallback(() => {
    if (isPhone) {
      navigate(`/${location.locationId}`);
    }
  }, [isPhone, location.locationId, navigate]);

  const { activePayments, addActivePayment, removeActivePayment, handlePaidOrFailedPayment } = useActivePayments({
    onPaymentCaptured,
  });

  const handleInitiatePaymentSuccess = useCallback(
    (payment: ListActivePaymentDto, onSuccess?: () => void) => async (data: InitiatedCartPaymentDto) => {
      if (payment.paymentMethod.paymentMethod === 'CASH') {
        await sendIosMessageCashPaymentSuccess(
          data.paymentAmount,
          data.paymentReference,
          location.locationId,
          location.currency,
          throwErrorToBoundary
        );
      }

      if (
        payment.paymentMethod.provider === 'SOFTPAY' &&
        !payment.paymentMethod.isUsingHub &&
        data.requestId &&
        softpayClient
      ) {
        softpayClient?.processPending(data.requestId, 3000, 'auto');
      }

      if (config.env.includes('local')) {
        try {
          await navigator.clipboard.writeText(data.paymentReference);
        } catch {
          // Clipboard access may fail in non-secure contexts
        }
      }

      const newPayment: ListActivePaymentDto = {
        cart: payment.cart,
        deviceId,
        paymentMethod: payment.paymentMethod,
        paymentReference: data.paymentReference,
        paymentState: data.paymentState,
      };

      // The active payment was added to the store without a reference in initiatePayment.
      // Update it now so removeActivePayment can match by paymentReference.
      useActivePaymentStore.getState().actions.updatePaymentReference(payment.paymentMethod.id, newPayment);

      // Track non-UUID integrated terminal payments via SSE/polling-driven state updates.
      // Skip subscription for already-settled payments (e.g. zero-amount direct sales).
      const isIntegrated = payment.paymentMethod.paymentMethod === 'INTEGRATED_TERMINAL';
      const alreadySettled = data.paymentState === PaymentState.SUCCEEDED || data.paymentState === PaymentState.FAILED;
      const subscribed =
        isIntegrated &&
        !alreadySettled &&
        subscribeToPayment({
          cart: payment.cart,
          onCompleted: (state) => handlePaidOrFailedPayment({ ...newPayment, paymentState: state }),
          paymentMethodId: payment.paymentMethod.id,
          paymentReference: data.paymentReference,
        });

      if (!subscribed) {
        handlePaidOrFailedPayment(newPayment);
      }

      setNewCart();

      onSuccess && onSuccess();
    },
    [
      deviceId,
      handlePaidOrFailedPayment,
      location.currency,
      location.locationId,
      setNewCart,
      subscribeToPayment,
      throwErrorToBoundary,
    ]
  );

  const handlePaymentError = useCallback(
    (payment: ListActivePaymentDto, onError?: () => void) => () => {
      removeActivePayment(payment);
      if (payment.paymentMethod.paymentMethod === 'INTEGRATED_TERMINAL') {
        useTerminalStore.getState().actions.clearOptimisticBusy(payment.paymentMethod.id);
      }
      onError && onError();
    },
    [removeActivePayment]
  );

  const initiatePayment = useCallback(
    async (props: InitiatePaymentArgs) => {
      const {
        paymentMethod,
        deliveryMethod,
        tableNumber,
        comment,
        onSuccess,
        onError,
        dynamicFields,
        giftCardPhoneNumber,
        tipAmount,
        vatType,
      } = props;

      const cart = getCartSnapshot();
      const effectiveTotal = getEffectiveTotalPrice(cart);

      const promptTips =
        paymentMethod.askForTip &&
        paymentMethod.provider === 'SOFTPAY' &&
        isTerminalModeEnabled &&
        !paymentMethod.isUsingHub;
      const terminalTipAmount = await promptForTips(promptTips, effectiveTotal, location.currency);

      const newPayment = {
        cart,
        deviceId,
        paymentMethod,
        paymentState: PaymentState.INITIATING,
      };
      addActivePayment(newPayment);

      const isIntegratedTerminal = paymentMethod.paymentMethod === 'INTEGRATED_TERMINAL';
      if (isIntegratedTerminal) {
        useTerminalStore.getState().actions.setOptimisticBusy(paymentMethod.id);
      }

      const resolvedDiscount = cart?.discountId
        ? discounts.find((discount) => discount.id === cart.discountId)
        : undefined;

      const payCartDto = toPayCartDto(
        {
          comment,
          deliveryMethod,
          deviceId,
          discount: resolvedDiscount,
          dynamicFields,
          giftCardPhoneNumber,
          paymentMethod,
          placementId: defaultPlacement,
          tableNumber,
          tipAmount: terminalTipAmount || tipAmount || 0,
          vatType,
        },
        cart
      );

      const initPendingPayment = () => {
        createPendingPayment({
          amount: effectiveTotal,
          askForTip: paymentMethod.askForTip,
          currency: location.currency,
          deviceId,
          locationId: location.locationId,
          onError: () => {
            setPaymentTracingActive(false);
            handlePaymentError(newPayment, onError)();
          },
          onSuccess: (response) => {
            setPaymentTracingActive(false);
            const mappedResponse: ListActivePaymentDto = {
              deviceId,
              paymentMethod,
              paymentReference: response.correlationId,
              paymentState: PaymentState.AWAITING_PAYMENT,
              cart: response.cartDto,
            };

            setNewCart();

            const handleSuccess = handleInitiatePaymentSuccess(mappedResponse, onSuccess);

            handleSuccess({
              paymentState: PaymentState.AWAITING_PAYMENT,
              paymentReference: response.correlationId,
              cartDto: response.cartDto!,
              paymentAmount: effectiveTotal,
            });
          },
          payCartDto,
          paymentMethodId: paymentMethod.id,
        });
      };

      const refetchActivePayments = () => {
        if (!isIntegratedTerminal) {
          queryClient.refetchQueries({ queryKey: ['active-payments', location.locationId] });
        }
      };

      const directSaleBaseData = {
        comment,
        deliveryMethodId: deliveryMethod.id,
        deviceId,
        discountCode: resolvedDiscount?.discountCodes[0],
        giftCardPhoneNumber,
        orderLines: cart.orderLines.map(toCreateOrderLineDto),
        paymentMethodId: paymentMethod.id,
        tableNumber,
        vatType,
      };

      const directSaleCallbacks = {
        onError: handlePaymentError(newPayment, onError),
        onSettled: () => {
          setPaymentTracingActive(false);
          refetchActivePayments();
        },
        onSuccess: (response: { paymentReference?: string; amountMinor?: number }) => {
          if (!response.paymentReference) {
            handlePaymentError(newPayment, onError)();
            throwErrorToBoundary(new Error('Missing payment reference from direct sale response'));
            return;
          }

          const isFreePayment = response.amountMinor === 0;
          if (isFreePayment && isIntegratedTerminal) {
            useTerminalStore.getState().actions.clearOptimisticBusy(paymentMethod.id);
          }

          handleInitiatePaymentSuccess(
            newPayment,
            onSuccess
          )({
            cartDto: cart,
            paymentState: isFreePayment ? PaymentState.SUCCEEDED : PaymentState.AWAITING_PAYMENT,
            paymentAmount: effectiveTotal,
            paymentReference: response.paymentReference,
          });
        },
      };

      const initHubPayment = () => {
        initiateDirectSaleMutation.mutate(
          {
            data: {
              ...directSaleBaseData,
              provider: 'SOFTPAY',
              tip: toDirectSaleTip(paymentMethod.askForTip),
            },
            locationId: location.id,
          },
          directSaleCallbacks
        );
      };

      const initAdyenDirectSale = () => {
        initiateDirectSaleMutation.mutate(
          {
            data: {
              ...directSaleBaseData,
              provider: 'ADYEN',
            },
            locationId: location.id,
          },
          directSaleCallbacks
        );
      };

      const initNetsDirectSale = () => {
        initiateDirectSaleMutation.mutate(
          {
            data: {
              ...directSaleBaseData,
              provider: 'NETS',
            },
            locationId: location.id,
          },
          directSaleCallbacks
        );
      };

      const initUnintegratedDirectSale = () => {
        initiateDirectSaleMutation.mutate(
          {
            locationId: location.id,
            data: {
              ...directSaleBaseData,
              provider: 'UNINTEGRATED',
              paymentMethod: paymentMethod.paymentMethod,
              unintegratedFields: dynamicFields,
            },
          },
          {
            ...directSaleCallbacks,
            onSuccess: (response: { paymentReference?: string }) => {
              if (!response.paymentReference) {
                handlePaymentError(newPayment, onError)();
                throwErrorToBoundary(new Error('Missing payment reference from direct sale response'));
                return;
              }

              handleInitiatePaymentSuccess(
                newPayment,
                onSuccess
              )({
                cartDto: cart,
                paymentState: PaymentState.SUCCEEDED,
                paymentAmount: effectiveTotal,
                paymentReference: response.paymentReference,
              });
            },
          }
        );
      };

      const initPayment = () => {
        payCartMutation.mutate(
          {
            payCartDto,
          },
          {
            onError: handlePaymentError(newPayment, onError),
            onSettled: () => {
              setPaymentTracingActive(false);
              refetchActivePayments();
            },
            onSuccess: handleInitiatePaymentSuccess(newPayment, onSuccess),
          }
        );
      };

      const shouldStartPendingPayment =
        paymentMethod.provider === 'SOFTPAY' && paymentMethod.askForTip && !isTerminalModeEnabled;

      const isAdyenTerminal =
        paymentMethod.provider === 'ADYEN' && paymentMethod.paymentMethod === 'INTEGRATED_TERMINAL';

      const isNetsTerminal = paymentMethod.provider === 'NETS' && paymentMethod.paymentMethod === 'INTEGRATED_TERMINAL';

      const isUnintegratedDirectSale =
        (paymentMethod.provider === 'UNINTEGRATED' && paymentMethod.paymentMethod === 'MOBILE_CARD_TERMINAL');

      const useDirectSaleEndpoint = paymentMethod.isUsingHub || isAdyenTerminal || isNetsTerminal || isUnintegratedDirectSale;
      setPaymentTracingActive(true, useDirectSaleEndpoint ? 'direct-sale' : 'default');

      if (paymentMethod.isUsingHub) {
        initHubPayment();
      } else if (isAdyenTerminal) {
        initAdyenDirectSale();
      } else if (isNetsTerminal) {
        initNetsDirectSale();
      } else if (isUnintegratedDirectSale) {
        initUnintegratedDirectSale();
      } else if (shouldStartPendingPayment) {
        initPendingPayment();
      } else {
        initPayment();
      }
    },
    [
      getCartSnapshot,
      isTerminalModeEnabled,
      promptForTips,
      location.currency,
      discounts,
      location.locationId,
      location.id,
      deviceId,
      addActivePayment,
      defaultPlacement,
      createPendingPayment,
      handlePaymentError,
      setNewCart,
      handleInitiatePaymentSuccess,
      initiateDirectSaleMutation,
      queryClient,
      payCartMutation,
      throwErrorToBoundary,
    ]
  );

  const contextValue = useMemo(
    () => ({
      activePayments,
      initiatePayment,
    }),
    [initiatePayment, activePayments]
  );

  return (
    <PaymentContext.Provider value={contextValue}>
      <>
        {children}
        {focusedPayment && (
          <FocusedPaymentDialog
            isOpen
            removeActivePayment={removeActivePayment}
            onCloseDialog={() => useFocusedPaymentStore.getState().setFocusedPayment(undefined)}
            focusedPayment={focusedPayment}
          />
        )}
        {TipsDialogUI}
      </>
    </PaymentContext.Provider>
  );
};

export const usePaymentContext = (): PaymentContextType => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePaymentContext must be used within a PaymentProvider');
  }
  return context;
};
